/**
 * BP + HR clinical classification for the back-office readings surfaces.
 * Bands are a guarded copy of WF3's IF chain / `Extract Values` (the eCKM/CKM
 * Care Pathways protocol) — static S-113 pins them against the workflow.
 */

/** @typedef {'hypotension'|'controlled'|'elevated'|'stage2'|'crisis'|'emergency'} BpClass */
/** @typedef {'bradycardia'|'normal'|'tachy_mild'|'tachy_severe'|'tachy_very_severe'} HrClass */
/** @typedef {{ epoch: number, grpid: number|string|null, systolic: number, diastolic: number, hr: number|null }} BpReading */

// Number(null) and Number('') are 0, which sails through Number.isFinite and makes a
// missing systolic read as 0 < 90 — an urgent "Low BP alert — ?/85".
const num = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));

/**
 * Classify a single BP reading. Order mirrors WF3's chain exactly:
 * CRISIS → STAGE 2 → HYPOTENSION → UNCONTROLLED. `emergency` is WF3's
 * `_band: emergency_200_140` sub-classification inside the crisis branch.
 *
 * @param {number|null|undefined} systolic
 * @param {number|null|undefined} diastolic
 * @returns {BpClass}
 */
export function classifyBp(systolic, diastolic) {
  const sys = num(systolic);
  const dia = num(diastolic);
  const hasSys = Number.isFinite(sys);
  const hasDia = Number.isFinite(dia);

  if ((hasSys && sys >= 180) || (hasDia && dia >= 110)) {
    return ((hasSys && sys >= 200) || (hasDia && dia >= 140)) ? 'emergency' : 'crisis';
  }
  if ((hasSys && sys >= 160) || (hasDia && dia >= 100)) return 'stage2';
  // Systolic-only — the protocol has no low-diastolic alert. No systolic means no
  // verdict: WF3 refuses to classify rather than let a null coerce to 0 < 90.
  if (hasSys && sys < 90) return 'hypotension';
  if ((hasSys && sys >= 130) || (hasDia && dia >= 80)) return 'elevated';
  return 'controlled';
}

/**
 * Classify a BPM-measured heart rate. Mirrors WF3 `Extract Values`, including the
 * 20-300 sanity band (outside it the value is a device artefact, not a finding).
 * Only pass HR that rode along with a BP pair — WF3 alerts on BPM readings only.
 *
 * @param {number|null|undefined} bpm
 * @returns {HrClass|null}
 */
export function classifyHr(bpm) {
  const v = num(bpm);
  if (!Number.isFinite(v) || v < 20 || v > 300) return null;
  if (v < 50) return 'bradycardia';
  if (v <= 100) return 'normal';
  if (v <= 120) return 'tachy_mild';
  if (v < 160) return 'tachy_severe';
  return 'tachy_very_severe';
}

// Protocol tier → what the care team owes. 1 = urgent clinician response,
// 2 = same business day, null = routine data review (no alert, no task).
/** @type {Record<BpClass, 1|2|null>} */
export const BP_CLASS_TIER = {
  hypotension: 1, controlled: null, elevated: null, stage2: 2, crisis: 1, emergency: 1,
};
/** @type {Record<HrClass, 1|2|null>} */
export const HR_CLASS_TIER = {
  bradycardia: 1, normal: null, tachy_mild: 2, tachy_severe: 1, tachy_very_severe: 1,
};

// Worst-first. Tier drives it, so hypotension (Tier 1) outranks Stage 2 (Tier 2).
const CLASS_ORDER = {
  emergency: 0, crisis: 1, hypotension: 2, stage2: 3, elevated: 4, controlled: 5,
};

// "Elevated" is the protocol's name for the Stage 2 / Tier 2 row, so it must never label
// an untiered band — that word on a 135/85 badge reads as "call today". S-113 pins it.
// Low → high, the order the protocol's own table reads in.
/** @type {BpClass[]} */
export const BP_DISPLAY_ORDER = [
  'hypotension', 'controlled', 'elevated', 'stage2', 'crisis', 'emergency',
];

/** @returns {Record<BpClass, number>} */
export const emptyBpBuckets = () => ({
  hypotension: 0, controlled: 0, elevated: 0, stage2: 0, crisis: 0, emergency: 0,
});

// Display labels per class — colors stay presentation-side (chart canvas / tokens).
/** @type {Record<BpClass, string>} */
export const BP_CLASS_LABELS = {
  controlled: 'Controlled',
  elevated: 'Mildly elevated',
  stage2: 'Stage 2+',
  hypotension: 'Hypotension',
  crisis: 'Crisis',
  emergency: 'Emergency',
};

/** @type {Record<HrClass, string>} */
export const HR_CLASS_LABELS = {
  bradycardia: 'Bradycardia',
  normal: 'Normal',
  tachy_mild: 'Tachycardia',
  tachy_severe: 'Tachycardia (severe)',
  tachy_very_severe: 'Tachycardia (very severe)',
};

// Protocol classification + clinic action, verbatim intent — surfaced on hover so
// the care team reads the same rule the workflow applied. `controlled` and `elevated`
// are ONE protocol band ("Normal / Controlled or mildly elevated", tier —); the split
// exists only because 130/80 is what WF3 counts toward the 7-day trend.
/** @type {Record<BpClass, string>} */
export const BP_CLASS_DETAIL = {
  hypotension: 'Under 90 systolic — hypotension. Tier 1: NP calls within 15 minutes.',
  controlled: '90/56–129/79 — within the acceptable home-monitoring range. Routine data review.',
  elevated: '130/80–159/99 — the upper half of the same acceptable range. Routine data review, no real-time intervention. Counts toward the 7-day trend.',
  stage2: '160/100–179/109 — Stage 2 hypertension. Tier 2: same-day NP outreach.',
  crisis: '180/110–199/139 — markedly elevated. Tier 1: evaluate for hypertensive emergency.',
  emergency: '200/140 or higher — hypertensive emergency. Tier 1: ED evaluation regardless of symptoms.',
};

/** @type {Record<HrClass, string>} */
export const HR_CLASS_DETAIL = {
  bradycardia: 'Under 50 bpm — bradycardia. Tier 1: assess rate-controlling medications.',
  normal: '50–100 bpm — routine data review.',
  tachy_mild: '101–120 bpm — mild tachycardia. Tier 2: same-day NP review.',
  tachy_severe: '121–159 bpm — severe tachycardia. Tier 1: immediate nurse outreach.',
  tachy_very_severe: '160 bpm or higher — very severe tachycardia. Tier 1: follow up after ED.',
};

// ONE definition of "worsening", shared by the BP card, the patient-home badge (via WF9's
// measure_state.trend_direction) and the trend alert. Windows mirror WF9 `trendDir` exactly;
// the band is absolute mmHg, not a percentage — 2% of 130 is 2.6 mmHg, inside home-BP
// day-to-day noise, so it called almost everything worsening. 8 mmHg is above the ~5 mmHg
// minimum clinically meaningful difference and above the ~8-10 mmHg day-to-day SD.
export const BP_TREND_RECENT_DAYS = 7;
export const BP_TREND_BASELINE_DAYS = 21; // outer edge; the baseline window is (21d, 7d]
export const BP_TREND_DELTA_MMHG = 8;

/**
 * Direction of travel of a patient's systolic, against their OWN recent baseline.
 *
 * Means are DAY-WEIGHTED (mean of per-day UTC means) — the key WF3 `Extract Values` and
 * WF9 `trendDir` use, so one multi-reading sitting cannot outweigh three single-reading
 * days. Rounded fields are display only; decisions use recentMean/baselineMean/deltaExact.
 * Spec 2026-09-09.
 *
 * @param {BpReading[]} readings ascending by epoch
 * @param {number} [nowMs]
 * @returns {{direction:'improving'|'stable'|'worsening'|'insufficient', delta:number|null,
 *            recentAvg:number|null, recentAvgDia:number|null, baselineAvg:number|null,
 *            recentCount:number, baselineCount:number,
 *            recentMean:number|null, baselineMean:number|null, deltaExact:number|null,
 *            recentDays:number, baselineDays:number}}
 */
export function classifyBpTrend(readings, nowMs = Date.now()) {
  const none = {
    direction: /** @type {const} */ ('insufficient'), delta: null,
    recentAvg: null, recentAvgDia: null, baselineAvg: null, recentCount: 0, baselineCount: 0,
    recentMean: null, baselineMean: null, deltaExact: null, recentDays: 0, baselineDays: 0,
  };
  if (!Array.isArray(readings) || readings.length < 3) return none;

  const recentFrom = nowMs - BP_TREND_RECENT_DAYS * 86400000;
  const baselineFrom = nowMs - BP_TREND_BASELINE_DAYS * 86400000;
  const recent = readings.filter(r => r.epoch * 1000 > recentFrom);
  const baseline = readings.filter(r => r.epoch * 1000 <= recentFrom && r.epoch * 1000 > baselineFrom);
  if (!recent.length || !baseline.length) return none;

  const dayMean = (a, k) => {
    const byDay = new Map();
    for (const r of a) {
      const day = new Date(r.epoch * 1000).toISOString().slice(0, 10);
      byDay.set(day, (byDay.get(day) || []).concat(r[k]));
    }
    let s = 0;
    for (const vals of byDay.values()) s += vals.reduce((x, y) => x + y, 0) / vals.length;
    return { mean: s / byDay.size, days: byDay.size };
  };
  const r = dayMean(recent, 'systolic');
  const b = dayMean(baseline, 'systolic');
  const deltaExact = r.mean - b.mean;
  // Direction off the EXACT delta. Rounding before a comparison is what made the alert's
  // floor fire at target - 0.5 (spec 2026-09-09); it has no place in one here either.
  const direction = Math.abs(deltaExact) < BP_TREND_DELTA_MMHG
    ? 'stable'
    : (deltaExact < 0 ? 'improving' : 'worsening');
  const recentAvg = Math.round(r.mean);
  const baselineAvg = Math.round(b.mean);
  return {
    // ONE rounding step, off the same quantity `direction` was decided on. The difference of
    // two separately rounded means drifts up to a full mmHg from the exact delta, which put
    // the card's number on the other side of the band from its own label ("Stable +8 mmHg").
    // A decimal is kept for the same reason: an integer near the edge re-creates that.
    direction, delta: Math.round(deltaExact * 10) / 10, recentAvg,
    recentAvgDia: Math.round(dayMean(recent, 'diastolic').mean), baselineAvg,
    recentCount: recent.length, baselineCount: baseline.length,
    recentMean: r.mean, baselineMean: b.mean, deltaExact,
    recentDays: r.days, baselineDays: b.days,
  };
}

/** True when the protocol assigns the class a tier, i.e. the care team owes a response. */
/** @param {BpClass} cls */
export const isBpAlerting = (cls) => BP_CLASS_TIER[cls] != null;
/** @param {HrClass|null} cls */
export const isHrAlerting = (cls) => cls != null && HR_CLASS_TIER[cls] != null;

/**
 * Pair systolic (type 10) and diastolic (type 9) readings; heart rate (type 11)
 * rides along when the session recorded one.
 * Strategy: group by grpid first (same measurement session), fall back to epoch.
 * Returns sorted ascending by epoch.
 *
 * @returns {BpReading[]}
 */
export function pairBpReadings(rawReadings) {
  const groups = new Map();
  for (const r of rawReadings) {
    const key = r.grpid || r.epoch;
    if (!groups.has(key)) groups.set(key, { epoch: r.epoch, grpid: r.grpid ?? null });
    const entry = groups.get(key);
    if (r.type === 10) { entry.systolic = r.value; entry.epoch = r.epoch; }
    if (r.type === 9)  entry.diastolic = r.value;
    // HR isn't guaranteed unit 0 like BP — apply Withings' value * 10^unit rule.
    if (r.type === 11) entry.hr = Math.round(r.value * (10 ** (r.unit ?? 0)));
  }

  const paired = [];
  for (const vals of groups.values()) {
    if (vals.systolic != null && vals.diastolic != null) {
      paired.push({
        epoch: vals.epoch,
        grpid: vals.grpid,
        systolic: vals.systolic,
        diastolic: vals.diastolic,
        hr: vals.hr ?? null,
      });
    }
  }
  return paired.sort((a, b) => a.epoch - b.epoch);
}

/**
 * Page index (0-based) holding the newest reading inside [startEpoch, endEpoch),
 * over a newest-first row list. 0 when nothing matches.
 */
export function findFocusPage(rowsDesc, focusRange, pageSize) {
  if (!focusRange || !rowsDesc.length || pageSize < 1) return 0;
  const [start, end] = focusRange;
  const idx = rowsDesc.findIndex(r => r.epoch >= start && r.epoch < end);
  return idx === -1 ? 0 : Math.floor(idx / pageSize);
}

/**
 * Compute clinical summary from paired BP readings.
 *
 * @param {BpReading[]} readings
 */
export function computeBpSummary(readings) {
  if (!readings.length) return null;

  const count = readings.length;
  const avgSystolic = Math.round(readings.reduce((s, r) => s + r.systolic, 0) / count);
  const avgDiastolic = Math.round(readings.reduce((s, r) => s + r.diastolic, 0) / count);

  // Classification counts
  const buckets = emptyBpBuckets();
  readings.forEach(r => { buckets[classifyBp(r.systolic, r.diastolic)]++; });

  // Dominant classification (majority bucket) — ties go to the worse class.
  const dominant = Object.entries(buckets)
    .sort((a, b) => (b[1] - a[1]) || (CLASS_ORDER[a[0]] - CLASS_ORDER[b[0]]))[0][0];

  // Trend — the shared definition, anchored on the newest reading so a stale window
  // behaves the same as it did before (the card renders history, not "now").
  const latestMs = readings[readings.length - 1].epoch * 1000;
  const t = classifyBpTrend(readings, latestMs);
  const trend = t.direction;
  const trendDelta = t.delta;
  const recentAvgVal = t.recentAvg;
  const priorAvgVal = t.baselineAvg;
  const recentAvgClass = t.recentAvg == null ? null : classifyBp(t.recentAvg, t.recentAvgDia);

  // Worst-first. Within a class the tiebreak follows the direction that class is
  // bad in — for hypotension a LOWER systolic is the worse reading.
  const bySeverity = (a, b) => {
    const ca = classifyBp(a.systolic, a.diastolic);
    const cb = classifyBp(b.systolic, b.diastolic);
    if (ca !== cb) return CLASS_ORDER[ca] - CLASS_ORDER[cb];
    return ca === 'hypotension' ? a.systolic - b.systolic : b.systolic - a.systolic;
  };
  const worst = [...readings].sort(bySeverity)[0];

  // Every reading the protocol assigns a tier to (Tier 1 + Tier 2), worst first —
  // consumers label alertReadings[0] as the worst, so the order is load-bearing.
  const alertReadings = readings
    .filter(r => isBpAlerting(classifyBp(r.systolic, r.diastolic)))
    .sort(bySeverity);

  return {
    count,
    avgSystolic,
    avgDiastolic,
    buckets,
    dominant,
    trend,
    trendDelta,
    recentAvgClass,
    recentAvg: recentAvgVal,
    priorAvg: priorAvgVal,
    worst,
    alertReadings,
    last5: readings.slice(-5).reverse(),
  };
}
